import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollAbility } from '../constants';
import useRafLoop from '../../../hooks/useRafLoop';
const hasYOverflow = ele => {
  return ele.scrollHeight > ele.clientHeight;
};
const hasScrolledToTheTop = ele => {
  return ele.scrollTop === 0;
};
const hasScrolledToTheBottom = ele => {
  return Math.abs(ele.scrollHeight - ele.scrollTop - ele.clientHeight) <= 4;
};
const useVerticalNavScrollControls = () => {
  const contentRef = useRef(null);
  const [showScrollControls, setShowScrollControls] = useState(false);
  const scrollIncrement = useRef(0);
  const maybeDisplayScrollControls = ele => {
    if (hasYOverflow(ele)) {
      if (ele.scrollTop === 0) {
        setShowScrollControls(ScrollAbility.down);
        return;
      }
      if (!hasScrolledToTheBottom(ele)) {
        setShowScrollControls(ScrollAbility.all);
        return;
      }
      setShowScrollControls(ScrollAbility.up);
      return;
    }
    setShowScrollControls(false);
  };
  const setContentRef = useCallback(ref => {
    if (ref) {
      contentRef.current = ref;
      maybeDisplayScrollControls(ref);
    }
  }, []);
  const scroll = useCallback(() => {
    if (!contentRef.current || !showScrollControls) return;
    const newScrollPosition = contentRef.current.scrollTop + scrollIncrement.current;
    if (newScrollPosition < contentRef.current.scrollHeight - contentRef.current.clientHeight) {
      contentRef.current.scroll(0, newScrollPosition);
    }
    maybeDisplayScrollControls(contentRef.current);
  }, [showScrollControls]);
  const [loopStop, loopStart] = useRafLoop(scroll, false);
  const startScrolling = useCallback(nextState => {
    if (nextState === ScrollAbility.up) {
      scrollIncrement.current = -5;
      loopStart();
      return;
    }
    if (nextState === ScrollAbility.down) {
      scrollIncrement.current = 5;
      loopStart();
      return;
    }
    scrollIncrement.current = 0;
    loopStop();
  }, [loopStart, loopStop]);
  const onScroll = useCallback(evt => {
    maybeDisplayScrollControls(evt.currentTarget);
  }, []);
  useEffect(() => {
    const resizeListener = () => {
      if (contentRef.current) {
        maybeDisplayScrollControls(contentRef.current);
      }
    };
    window.addEventListener('resize', resizeListener);
    return () => {
      return window.removeEventListener('resize', resizeListener);
    };
  }, []);
  useEffect(() => {
    if (showScrollControls === ScrollAbility.up && contentRef.current && hasScrolledToTheBottom(contentRef.current) || showScrollControls === ScrollAbility.down && contentRef.current && hasScrolledToTheTop(contentRef.current)) {
      loopStop();
    }
  }, [loopStop, showScrollControls]);
  return {
    setContentRef,
    showTopScroll: showScrollControls === ScrollAbility.all || showScrollControls === ScrollAbility.up,
    showDownScroll: showScrollControls === ScrollAbility.all || showScrollControls === ScrollAbility.down,
    startScrolling,
    onScroll
  };
};
export default useVerticalNavScrollControls;